import { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { contactApi } from '@/lib/api';

const IletisimPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Lütfen gerekli alanları doldurun.');
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error('Geçerli bir email adresi girin.');
      return;
    }

    setIsSubmitting(true);

    try {
      await contactApi.sendMessage(formData);
      setIsSubmitted(true);
      toast.success('Mesajınız başarıyla iletildi.');
    } catch (error) {
      console.error('Failed to send contact message:', error);
      const msg = error.response?.data?.detail || 'Mesaj gönderilirken bir hata oluştu.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Layout>
        <section className="container-wide py-20 sm:py-28">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
            <h1 className="heading-section text-foreground mb-4">Teşekkürler!</h1>
            <p className="text-muted-foreground mb-8">
              Mesajınız başarıyla iletildi. En kısa sürede size dönüş yapacağız.
            </p>
            <Button 
              onClick={() => {
                setIsSubmitted(false);
                setFormData({ name: '', email: '', subject: '', message: '' });
              }}
              variant="outline"
            >
              Yeni Mesaj Gönder
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="container-wide py-12 sm:py-16">
        <div className="max-w-2xl">
          <span className="caption text-accent mb-2 block">İletişim</span>
          <h1 className="heading-article text-foreground mb-4">Bize Ulaşın</h1>
          <p className="body-large text-muted-foreground">
            Sorularınız, önerileriniz veya işbirliği teklifleriniz için iletişime geçebilirsiniz.
          </p>
        </div>
      </section>

      {/* Horizontal Rule */}
      <div className="container-wide">
        <div className="h-px bg-border" />
      </div>

      {/* Content */}
      <section className="container-wide py-12 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Ad Soyad *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Adınız Soyadınız"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-background border-border"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-background border-border"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Konu</Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="Mesajınızın konusu"
                  value={formData.subject}
                  onChange={handleChange}
                  className="bg-background border-border"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Mesaj *</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Mesajınızı yazın..."
                  value={formData.message}
                  onChange={handleChange}
                  className="bg-background border-border min-h-[180px]"
                  required
                />
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Mesaj Gönder
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 space-y-6">
              {/* Contact Info */}
              <div className="p-6 bg-card border border-border rounded-sm">
                <h3 className="font-serif font-bold text-foreground mb-6">İletişim Bilgileri</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-accent mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Email</p>
                      <a 
                        href="mailto:hello@betweenlines.com" 
                        className="text-sm text-muted-foreground hover:text-accent transition-colors"
                      >
                        hello@betweenlines.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-accent mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Konum</p>
                      <p className="text-sm text-muted-foreground">
                        İstanbul, Türkiye
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div className="p-6 bg-muted/30 border border-border rounded-sm">
                <h3 className="font-serif font-bold text-foreground mb-4">Sosyal Medya</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Güncel paylaşımlar ve tartışmalar için sosyal medyada takip edin.
                </p>
                <div className="flex gap-3">
                  <a 
                    href="https://twitter.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-sm hover:bg-muted transition-colors"
                  >
                    Twitter / X
                  </a>
                  <a 
                    href="https://linkedin.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-sm hover:bg-muted transition-colors"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>

              {/* Note */}
              <p className="text-xs text-muted-foreground">
                * İşaretli alanlar zorunludur. Mesajınıza genellikle 2-3 iş günü içinde dönüş yapılır.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
};

export default IletisimPage;
